
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface NotificationSettingsData {
  id?: number;
  reminder_days_long: number;
  reminder_days_medium: number;
  reminder_days_short: number;
  reminder_days_critical: number;
  send_hour: number;
  is_enabled: boolean;
  email_template: string;
  created_at?: string;
  updated_at?: string;
}

export const useNotificationSettings = () => {
  // Modifier les valeurs par défaut pour les délais d'expiration
  const [settings, setSettings] = useState<NotificationSettingsData>({
    reminder_days_long: 90,     // Augmenté de 60 à 90 jours
    reminder_days_medium: 45,   // Modifié de 30 à 45 jours
    reminder_days_short: 15,    // Modifié de 7 à 15 jours
    reminder_days_critical: 3,  // Modifié de 1 à 3 jours
    send_hour: 9,               // Modifié de 8 à 9 heures
    is_enabled: true,
    email_template: 'default'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await (supabase as any)
        .from('notification_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSettings({
          id: data.id,
          reminder_days_long: data.reminder_days_long ?? 90,
          reminder_days_medium: data.reminder_days_medium ?? 45,
          reminder_days_short: data.reminder_days_short ?? 15,
          reminder_days_critical: data.reminder_days_critical ?? 3,
          send_hour: data.send_hour ?? 9,
          is_enabled: data.is_enabled ?? true,
          email_template: data.email_template ?? 'default',
          created_at: data.created_at,
          updated_at: data.updated_at
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      
      const { data: existingSettings } = await (supabase as any)
        .from('notification_settings')
        .select('id')
        .single();

      if (existingSettings) {
        const { error } = await (supabase as any)
          .from('notification_settings')
          .update({
            reminder_days_long: settings.reminder_days_long,
            reminder_days_medium: settings.reminder_days_medium,
            reminder_days_short: settings.reminder_days_short,
            reminder_days_critical: settings.reminder_days_critical,
            send_hour: settings.send_hour,
            is_enabled: settings.is_enabled,
            email_template: settings.email_template,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSettings.id);

        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from('notification_settings')
          .insert({
            reminder_days_long: settings.reminder_days_long,
            reminder_days_medium: settings.reminder_days_medium,
            reminder_days_short: settings.reminder_days_short,
            reminder_days_critical: settings.reminder_days_critical,
            send_hour: settings.send_hour,
            is_enabled: settings.is_enabled,
            email_template: settings.email_template
          });

        if (error) throw error;
      }

      toast.success('Paramètres sauvegardés avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setIsSaving(false);
    }
  };

  const testNotificationSystem = async () => {
    try {
      toast.info('Test du système de notification en cours...');
      
      const { data, error } = await supabase.functions.invoke('check-expired-products', {
        body: { test: true }
      });

      if (error) throw error;

      toast.success(`Test terminé: ${data.message}`);
    } catch (error) {
      console.error('Erreur lors du test:', error);
      toast.error('Erreur lors du test du système de notification');
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return {
    settings,
    setSettings,
    isLoading,
    isSaving,
    saveSettings,
    testNotificationSystem
  };
};
